import { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend,
} from 'recharts';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import type { CounselorPerfRow, LeadConversionRow, AppointmentSummaryRow } from '@/lib/types';
import { titleCase } from '@/lib/format';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (ym: string) => MONTHS[Number(ym.split('-')[1]) - 1] ?? ym;
const funnelOrder = ['new', 'contacted', 'interested', 'follow-up', 'converted', 'lost'];

const reportCards = [
  { id: 'counselor', title: 'Counselor Performance', description: 'Students and conversions per counselor', icon: 'ri-user-star-line', color: 'bg-blue-50 text-blue-600' },
  { id: 'lead', title: 'Lead Conversion', description: 'Distribution of leads across pipeline stages', icon: 'ri-filter-3-line', color: 'bg-amber-50 text-amber-600' },
  { id: 'appointment', title: 'Appointment Summary', description: 'Booked, completed and cancelled by month', icon: 'ri-calendar-check-line', color: 'bg-purple-50 text-purple-600' },
];

function CounselorReport() {
  const { data, loading } = useApiData<CounselorPerfRow[]>(() => api.counselorPerformance());
  const rows = (data ?? []).map((r) => ({ ...r, students: Number(r.students), converted: Number(r.converted), rate: Number(r.students) > 0 ? Math.round(Number(r.converted) / Number(r.students) * 100) : 0 }));
  if (loading) return <div className="py-8 text-center text-sm text-gray-400">Loading…</div>;
  if (rows.length === 0) return <div className="py-8 text-center text-sm text-gray-400">No data yet.</div>;
  return (
    <div className="space-y-4">
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={rows} barSize={20}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', fontSize: '12px' }} />
          <Legend />
          <Bar dataKey="students" fill="#0B6E77" radius={[3, 3, 0, 0]} name="Students" />
          <Bar dataKey="converted" fill="#0B6E77" radius={[3, 3, 0, 0]} name="Converted" />
        </BarChart>
      </ResponsiveContainer>
      <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-[#E1E5EA] bg-gray-50">{['Counselor', 'Students', 'Converted', 'Conversion Rate'].map((h) => <th key={h} className="text-left text-xs font-semibold text-gray-500 px-3 py-2 whitespace-nowrap">{h}</th>)}</tr></thead><tbody className="divide-y divide-gray-50">{rows.map((row) => <tr key={row.name} className="hover:bg-gray-50"><td className="px-3 py-2 text-sm font-medium text-gray-800 whitespace-nowrap">{row.name}</td><td className="px-3 py-2 text-sm text-gray-600">{row.students}</td><td className="px-3 py-2 text-sm text-gray-600">{row.converted}</td><td className="px-3 py-2"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${row.rate >= 50 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{row.rate}%</span></td></tr>)}</tbody></table></div>
    </div>
  );
}

function LeadReport() {
  const { data, loading } = useApiData<LeadConversionRow[]>(() => api.leadConversion());
  const map = new Map((data ?? []).map((r) => [(r.status || '').toLowerCase(), Number(r.count)]));
  const rows = funnelOrder.filter((s) => map.has(s)).map((s) => ({ stage: titleCase(s), count: map.get(s) || 0 }));
  const extra = (data ?? []).filter((r) => !funnelOrder.includes((r.status || '').toLowerCase())).map((r) => ({ stage: titleCase(r.status), count: Number(r.count) }));
  const all = [...rows, ...extra];
  if (loading) return <div className="py-8 text-center text-sm text-gray-400">Loading…</div>;
  if (all.length === 0) return <div className="py-8 text-center text-sm text-gray-400">No leads yet.</div>;
  return (
    <div className="space-y-4">
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={all} layout="vertical" barSize={18}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
          <YAxis dataKey="stage" type="category" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={80} />
          <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', fontSize: '12px' }} />
          <Bar dataKey="count" fill="#0B6E77" radius={[0, 3, 3, 0]} name="Leads" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function AppointmentReport() {
  const { data, loading } = useApiData<AppointmentSummaryRow[]>(() => api.appointmentSummary());
  const rows = (data ?? []).map((r) => ({ month: monthLabel(r.ym), booked: Number(r.booked), completed: Number(r.completed), cancelled: Number(r.cancelled) }));
  if (loading) return <div className="py-8 text-center text-sm text-gray-400">Loading…</div>;
  if (rows.length === 0) return <div className="py-8 text-center text-sm text-gray-400">No appointments yet.</div>;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={rows}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', fontSize: '12px' }} />
        <Legend />
        <Line type="monotone" dataKey="booked" stroke="#0B6E77" strokeWidth={2} dot={{ r: 3 }} name="Booked" />
        <Line type="monotone" dataKey="completed" stroke="#0B6E77" strokeWidth={2} dot={{ r: 3 }} name="Completed" />
        <Line type="monotone" dataKey="cancelled" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} name="Cancelled" />
      </LineChart>
    </ResponsiveContainer>
  );
}

const reportComponents: Record<string, React.FC> = { counselor: CounselorReport, lead: LeadReport, appointment: AppointmentReport };

export default function ReportsPage() {
  const [activeReport, setActiveReport] = useState<string | null>('counselor');
  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {reportCards.map((card) => (
            <div key={card.id} className="bg-white rounded-xl border border-[#E1E5EA] p-5">
              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${card.color.split(' ')[0]}`}><i className={`${card.icon} text-xl ${card.color.split(' ')[1]}`} /></div>
                <div className="flex-1"><h3 className="text-sm font-semibold text-[#3C3C3C]">{card.title}</h3><p className="text-xs text-gray-500 mt-0.5">{card.description}</p></div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button onClick={() => setActiveReport(activeReport === card.id ? null : card.id)} className="flex-1 py-2 text-xs font-semibold bg-[#0B6E77] text-white rounded-lg hover:bg-[#085258] transition-colors cursor-pointer whitespace-nowrap">{activeReport === card.id ? 'Hide Report' : 'View Report'}</button>
              </div>
              {activeReport === card.id && (
                <div className="mt-4 pt-4 border-t border-[#E1E5EA]">
                  {(() => { const R = reportComponents[card.id]; return R ? <R /> : null; })()}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
